import { Button } from "@/src/components/Button";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import { CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { Suspense, useState } from "react";
import { OtpField } from "@/src/components/Fields/OtpField";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { TextWithTimer } from "@/src/components/TextWithTimer";
import { strings } from "@/src/shared/strings";
import { useHandleApiCall } from "@/src/components/pages/StepPage/components/apiCalls/useHandleApiCall";
import { ApiCallsTypes, leadActionNameMap, PREMIUM_INTEGRITY_MISMATCH_ERROR_CODE } from "@/src/components/pages/StepPage/components/apiCalls/contants";
import {
  apiCallsModalStore,
  openLoadingModal,
  openPremiumIntegrityErrorModal,
} from "@/src/components/pages/StepPage/components/apiCalls/modalStore";
import {
  ApiCallModalType,
  OTPModalData,
} from "@/src/components/pages/StepPage/components/apiCalls/types";
import { isString, size } from "lodash";
import { useOtpTemplateCode } from "@/src/components/pages/StepPage/components/apiCalls/Modals/useOtpTemplateCode";
import { maskFunctions } from "@/src/shared/utils/mask";
import { useAnalytics } from "@/src/components/pages/StepPage/components/useAnalytics/useAnalytics";
import { ModalHeader } from "./components/ModalHeader";
import { isMobile } from "react-device-detect";
import { useUpdateField } from "@/src/hooks/useFieldDebouncedCallback";
import { getObjectSignCodeProduct } from "@/src/components/Action/useFormDataMiddleware";
import { loadingModalDefaultProps } from "@/src/components/pages/StepPage/components/apiCalls/Modals/defaultModalsProps";
import { logError } from "@/src/api/server-actions/analytics";

/*
Example of the errors that can be returned from the API:
{
    "errorCode": "2020300204",
    "errorDescription": "Код ідентифікації некоректний",
    "errorType": "BusinessError"
}
{
    "errorCode": "2020300105",
    "errorDescription": "Запити занадто часті. Спробуйте ще раз пізніше",
    "errorType": "BusinessError"
}
{
    "errorCode": "2020300204",
    "errorDescription": "Незазначені обов'язкові поля",
    "errorType": "BusinessError"
}
 */

export const OTPModal = ({
  identificationCodeId,
  controlCode,
  errors,
}: OTPModalData) => {
  const { trackAnalyticEvent } = useAnalytics();
  const { handleApiCallByName: handleActionByName } = useHandleApiCall(false);
  const { handleApiCallByName } = useHandleApiCall(true);
  const { getValues, setValue } = useFormContext();
  const form = useForm({
    defaultValues: { controlCode, phoneNumberCode: "" },
  });
  const { close, open } = apiCallsModalStore();
  const templateCode = useOtpTemplateCode();
  const updateField = useUpdateField();

  const phoneNumber = getValues("phoneNumber");
  const [isResendAvailable, setIsResendAvailable] = useState(false);
  const [isSubmitPending, setIsSubmitPending] = useState(false);

  const onCancel = () => {
    trackAnalyticEvent("sign_cancel");
    close();
    form.reset();
  };
  const onSubmit = async () => {
    trackAnalyticEvent("sign_contract");
    try {
      setIsSubmitPending(true);
      const response = await handleApiCallByName(
        ApiCallsTypes.VERIFY_IDENTIFICATION_CODE,
        true,
        {
          formDataOverride: {
            ...getValues(),
            identificationCodeId,
            codeToVerify: form.getValues("phoneNumberCode"),
          },
        },
      );

      setValue('codeToVerify', form.getValues("phoneNumberCode"))

      if (!response.isCodeVerified || size(response?.errors)) {
        form.setError("phoneNumberCode", {
          message: response?.errors?.[0]?.errorDescription,
        });
        setIsSubmitPending(false);
        return;
      }

      // force update lead
      const flowCode = getValues("flowCode");
      const leadActionName = flowCode && leadActionNameMap[flowCode];
      if (leadActionName) {
        await handleActionByName(leadActionName, true);
      }
      // end force update lead

      await updateField(
        "objectSignCode_Product",
        getObjectSignCodeProduct(getValues()),
      );

      const currentUrl = window.location.href;
      setValue("billingRedirectUrl", currentUrl);

      const data = await handleActionByName(ApiCallsTypes.IMPORT_BILL, true);
      const deepLink = data?.contextInstance?.variables?.find(
        (variable: { key: string }) => variable.key === "redirectUrl",
      )?.value;
      if (deepLink) {
        console.log("billing deepLink", deepLink);
        window.location.href = deepLink;
      }
      close();
      openLoadingModal({
        title: loadingModalDefaultProps.title,
        description: "Опрацьовуємо ваш запит",
        disableClose: true,
      });
      form.reset();
    } catch (error: any) {
      logError('[OTPModal] onSubmit error', error);
      console.log("SignAgreementStage error: ", error);

      // The importBill action refused to create the bill because the premium is stale/inconsistent
      // with the picked package. Branch on the response CONTENT (errorCode), not the HTTP status,
      // and show the standard technical-error modal ONCE. We deliberately do not surface this as an
      // OTP field error and do not open the "Опрацьовуємо ваш запит" loader: the client closes the
      // modal and can retry «Купити», which re-tariffies and recalculates the premium.
      if (
          error?.response?.data?.errorCode ===
          PREMIUM_INTEGRITY_MISMATCH_ERROR_CODE
      ) {
        openPremiumIntegrityErrorModal();
        setIsSubmitPending(false);
        return;
      }

      const serverErrorMessage = error?.response?.data?.message;
      form.setError("phoneNumberCode", {
        message: isString(serverErrorMessage)
          ? serverErrorMessage
          : "Щось пішло не так",
      });
    }
    setIsSubmitPending(false);
  };

  const hideResend = () => {
    setIsResendAvailable(false);
  };
  const showResend = () => {
    setIsResendAvailable(true);
  };

  const resendCode = async () => {
    form.setValue("phoneNumberCode", "");
    try {
      const response = await handleApiCallByName(
        ApiCallsTypes.SEND_IDENTIFICATION_CODE,
        true,
        {
          formDataOverride: {
            ...getValues(),
            templateCode: templateCode,
          },
        },
      );
      if (size(response?.errors)) {
        form.setError("phoneNumberCode", {
          message: response?.errors?.[0]?.errorDescription,
        });
        return;
      }
      if (response) {
        open(ApiCallModalType.OTP, response);
        hideResend();
      }
    } catch (e) {
      logError('[OTPModal] resendCode error', e);
      console.error(e);
    }
  };

  return (
    <FormProvider {...form}>
      <Stack
        className={
          "max-w-[524px] rounded-xl bg-white center-modal max-h-full max-md:animate-up-modal max-md:items-start max-md:rounded-none max-md:bottom-modal"
        }
      >
        {isMobile && (
          <div className="h-14 w-full md:hidden">
            <ModalHeader onCancel={onCancel} />
          </div>
        )}
        <div className="flex flex-col items-center gap-4 p-6 max-md:h-full flex-1 overflow-y-auto">
          <Stack className={"w-full flex-center-row"}>
            <div className="flex flex-1 md:justify-center">
              <Typography
                variant={"h1"}
                component={"h3"}
                className={
                  "flex-0 w-fit justify-center text-center text-2xl font-semibold max-md:text-start"
                }
              >
                {"Підписання договору"}
              </Typography>
            </div>
            <IconButton onClick={onCancel} className="max-md:hidden">
              <WebShopIcon name={"close"} className="fill-blue" />
            </IconButton>
          </Stack>
          <Stack className="flex flex-col">
            {phoneNumber && (
              <Typography
                component={"p"}
                className={"text-center max-md:text-start"}
              >
                На Ваш номер телефону{" "}
                <Typography component="span" className="inline font-semibold">
                  {maskFunctions.formatPhoneNumber(phoneNumber)}
                </Typography>{" "}
                надійшов код для підпису електронного полісу.
              </Typography>
            )}
          </Stack>
          <Stack className="flex flex-col gap-4">
            <OtpField control={form.control} name={"phoneNumberCode"} />
            {errors?.length > 0 && (
              <div className="text-xs font-normal text-red">
                {errors[0]?.errorDescription}
              </div>
            )}
            {controlCode ? (
              <div className="flex items-center gap-1">
                <Typography className="font-semibold">
                  Контрольний код: {controlCode}
                </Typography>
              </div>
            ) : null}
            {isResendAvailable ? (
              <Typography
                variant="body1"
                className={"cursor-pointer"}
                onClick={resendCode}
              >
                Надіслати код повторно
              </Typography>
            ) : (
              <TextWithTimer onTimerEnd={showResend} />
            )}
          </Stack>
          <Stack className="w-full justify-end gap-3 flex-center-row max-md:mt-auto">
            <Button
              onClick={onCancel}
              variant="outlined"
              className="w-fit max-md:w-full"
            >
              {strings.common.revoke}
            </Button>
            <Button
              onClick={onSubmit}
              loading={isSubmitPending}
              disabled={!controlCode}
              className="w-fit max-md:w-full"
            >
              {"Підписати"}
            </Button>
          </Stack>
        </div>
      </Stack>
    </FormProvider>
  );
};
